import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { sha256 } from '@rhg/utils/string';
import { useCallback } from 'react';
import { RegistrationFlow } from '../use-registration-context/types';

const PreviousRegistrationFlowKey = 'prf';

interface PreviousRegistrationFlowData {
    localStorageKey: string | null;
    previousRegistrationFlow: RegistrationFlow | null;
}
const placeholderData = {
    localStorageKey: null,
    previousRegistrationFlow: null,
};
export function usePreviousRegistrationFlow() {
    const { isAuthenticated, user } = useAuth();

    const { data, isFetched, ...otherProps } =
        useQuery<PreviousRegistrationFlowData>({
            queryKey: [queryKeys.previousRegistrationFlow, user?.sub],
            queryFn: async () => {
                if (isAuthenticated && user?.sub) {
                    const localStorageKey = `${PreviousRegistrationFlowKey}-${await sha256(
                        user?.sub
                    )}`;

                    // eslint-disable-next-line no-restricted-syntax
                    const localStorageItem = localStorage.getItem(
                        localStorageKey
                    ) as RegistrationFlow;

                    const previousRegistrationFlow = Object.values(
                        RegistrationFlow
                    ).includes(localStorageItem)
                        ? localStorageItem
                        : null;

                    return { localStorageKey, previousRegistrationFlow };
                }
                return placeholderData;
            },
            enabled: isAuthenticated && !!user?.sub,
            staleTime: 1000 * 60 * 60 * 8,
        });

    const setPreviousRegistrationFlow = useCallback(
        (value: RegistrationFlow) => {
            if (isFetched && data?.localStorageKey) {
                // eslint-disable-next-line no-restricted-syntax
                localStorage.setItem(data?.localStorageKey, value);
                return;
            }
            throw new Error(
                'setPreviousRegistrationFlow: hook must be fetched before it can be called'
            );
        },
        [data?.localStorageKey, isFetched]
    );

    const clearPreviousRegistrationFlow = useCallback(() => {
        if (isFetched && data?.localStorageKey) {
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(data?.localStorageKey);
            return;
        }
        throw new Error(
            'clearPreviousRegistrationFlow: hook must be fetched before it can be called'
        );
    }, [data?.localStorageKey, isFetched]);

    return {
        ...otherProps,
        ...(data ?? placeholderData),
        isFetched,
        /** Sets the previous registration flow we must wait for hook isFetched to be true */
        setPreviousRegistrationFlow,
        /** Clears the previous registration flow we must wait for hook isFetched to be true */
        clearPreviousRegistrationFlow,
    };
}
